'use strict';

var currentWishlistProductData = null;
var pdpProductData = null;

/**
 * Returns the latest PDP product payload used by the wishlist drawer.
 * @returns {Object|null} Product data or null when unavailable
 */
function getWishlistProductData() {
    return pdpProductData;
}

/**
 * Returns the currently active wishlist product payload.
 * @returns {Object|null} Active wishlist product data
 */
function getCurrentWishlistProductData() {
    return currentWishlistProductData || getWishlistProductData();
}

/**
 * Extracts a query parameter value from a variation URL.
 * @param {string} url - Variation URL
 * @param {string} key - Query parameter name
 * @returns {string|null} Query parameter value when present
 */
function getQueryParamValue(url, key) {
    var queryString;
    var pairs;
    var i;
    var pair;

    if (!url || url.indexOf('?') === -1) {
        return null;
    }

    queryString = url.split('?')[1].split('#')[0];
    pairs = queryString.split('&');

    for (i = 0; i < pairs.length; i++) {
        pair = pairs[i].split('=');
        if (decodeURIComponent(pair[0]) === key) {
            return pair.length > 1 ? decodeURIComponent(pair[1]) : '';
        }
    }

    return null;
}

/**
 * Finds a variation attribute from the current wishlist product payload.
 * @param {string} attributeId - Variation attribute id
 * @returns {Object|null} Matching variation attribute
 */
function getWishlistVariationAttribute(attributeId) {
    var wishlistProductData = getCurrentWishlistProductData();
    var variationAttributes = wishlistProductData && wishlistProductData.variationAttributes
        ? wishlistProductData.variationAttributes
        : [];
    var matchingAttribute = null;

    variationAttributes.forEach(function (attr) {
        if (!matchingAttribute && attr.id === attributeId) {
            matchingAttribute = attr;
        }
    });

    return matchingAttribute;
}

/**
 * Finds a selected variation attribute value.
 * @param {string} attributeId - Variation attribute id
 * @returns {Object|null} Selected attribute value
 */
function getSelectedWishlistAttributeValue(attributeId) {
    var attribute = getWishlistVariationAttribute(attributeId);
    var selectedValue = null;

    if (!attribute || !attribute.values) {
        return null;
    }

    attribute.values.forEach(function (value) {
        if (!selectedValue && value.selected) {
            selectedValue = value;
        }
    });

    return selectedValue;
}

/**
 * Finds the first selectable variation attribute value.
 * @param {string} attributeId - Variation attribute id
 * @returns {Object|null} First selectable attribute value
 */
function getFirstSelectableWishlistAttributeValue(attributeId) {
    var attribute = getWishlistVariationAttribute(attributeId);
    var selectableValue = null;

    if (!attribute || !attribute.values) {
        return null;
    }

    attribute.values.forEach(function (value) {
        if (!selectableValue && value.selectable) {
            selectableValue = value;
        }
    });

    return selectableValue;
}

/**
 * Updates the current wishlist product payload from a variation URL.
 * @param {string} variationUrl - Variation URL containing the target pid
 * @param {Function} callback - Invoked with true when state updates successfully
 */
function updateWishlistProductDataFromUrl(variationUrl, callback) {
    var targetPid = getQueryParamValue(variationUrl, 'pid');
    var baseProductData = getWishlistProductData();

    if (!targetPid || !baseProductData) {
        callback(false);
        return;
    }

    $.ajax({
        url: variationUrl,
        type: 'get',
        dataType: 'json',
        success: function (response) {
            if (response && response.product) {
                currentWishlistProductData = response.product;
            }

            callback(!!currentWishlistProductData);
        },
        error: function () {
            callback(false);
        }
    });
}

/**
 * Ensures the wishlist drawer has a usable default variation selected.
 * @param {Function} callback - Invoked after default selection resolution completes
 */
function ensureWishlistDefaultSelection(callback) {
    var selectedColorValue = getSelectedWishlistAttributeValue('color');
    var colorAttribute = getWishlistVariationAttribute('color');
    var fallbackValue = null;
    var needsColorSelection = !!colorAttribute && !selectedColorValue;

    if (!needsColorSelection) {
        callback();
        return;
    }

    fallbackValue = getFirstSelectableWishlistAttributeValue('color');

    if (!fallbackValue || !fallbackValue.url) {
        callback();
        return;
    }

    updateWishlistProductDataFromUrl(fallbackValue.url, function () {
        callback();
    });
}

/**
 * Builds a color option button for the wishlist drawer.
 * @param {Object} attrValue - Variation attribute value data
 * @returns {HTMLButtonElement} Button node
 */
function createColorButton(attrValue) {
    var button = document.createElement('button');
    var content = document.createElement('div');
    var swatch = document.createElement('span');
    var colorName = document.createElement('span');
    var className = 'dropdown-item js-dropdown-color';

    if (!attrValue.selectable) {
        className += ' disabled';
    }

    if (attrValue.selected) {
        className += ' selected';
    }

    button.className = className;
    button.type = 'button';
    button.setAttribute('data-url', attrValue.url || '');
    button.setAttribute('data-attr-value', attrValue.value);

    content.className = 'special-select-item';
    swatch.className = 'swatch-circle';

    if (!attrValue.swatchColors || attrValue.isLightColorSwatch) {
        swatch.className += ' swatch-white';
    }

    if (attrValue.swatchColors && attrValue.swatchColors.length) {
        swatch.style.background = attrValue.swatchColors[1]
            ? 'linear-gradient(to right, ' + attrValue.swatchColors[0] + ' 70%, ' + attrValue.swatchColors[1] + ' 0)'
            : attrValue.swatchColors[0];
    }

    colorName.className = 'color-name';
    colorName.textContent = attrValue.displayValue;

    content.appendChild(swatch);
    content.appendChild(colorName);
    button.appendChild(content);

    return button;
}

/**
 * Appends a grouped set of color buttons into the wishlist dropdown.
 * @param {HTMLElement} dropdownMenu - Dropdown menu container
 * @param {string} label - Optional group label
 * @param {string} rangeText - Optional price or range text
 * @param {Array} values - Color values in the group
 */
function appendColorGroup(dropdownMenu, label, rangeText, values) {
    var header;
    var title;
    var range;
    var isWarningGroup = label && (label.indexOf('SALE') !== -1 || label.indexOf('CLEARANCE') !== -1);

    if (!values || !values.length) {
        return;
    }

    header = document.createElement('div');
    header.className = 'special-select-group-header' + (isWarningGroup ? ' text-warning' : '');

    if (label) {
        title = document.createElement('span');
        title.className = 'color non-input-label d-block mb-0';
        title.textContent = label;
        header.appendChild(title);
    }

    if (rangeText) {
        range = document.createElement('span');
        range.className = 'dropdown-group-price';
        range.innerHTML = rangeText;
        header.appendChild(range);
    }

    dropdownMenu.appendChild(header);
    values.forEach(function (value) {
        dropdownMenu.appendChild(createColorButton(value));
    });
}

/**
 * Renders the wishlist color selector from product variation data.
 * @param {Object} attr - Color variation attribute
 * @param {string|null} selectedColor - Currently selected color
 * @returns {HTMLDivElement} Rendered color attribute wrapper
 */
function renderColorAttribute(attr, selectedColor) {
    var wrapper = document.createElement('div');
    var label = document.createElement('label');
    var dropdown = document.createElement('div');
    var toggle = document.createElement('button');
    var toggleItem = document.createElement('div');
    var toggleText = document.createElement('span');
    var menu = document.createElement('div');
    var selectedValue = null;

    wrapper.className = 'attribute mb-3';
    wrapper.setAttribute('data-attr', 'wishlist-' + attr.id);

    label.className = attr.id + ' label';
    label.textContent = attr.displayName;

    dropdown.className = 'dropdown special-select wishlist-special-select';
    toggle.className = 'special-select-toggle';
    toggle.type = 'button';
    toggle.id = 'colorDropdownWishlist';
    toggle.setAttribute('data-toggle', 'dropdown');
    toggle.setAttribute('aria-haspopup', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    toggle.setAttribute('data-select-text', 'Choose Color');

    toggleItem.className = 'special-select-item';
    toggleText.className = 'color-name';
    toggleText.textContent = 'Choose Color';
    toggleItem.appendChild(toggleText);
    toggle.appendChild(toggleItem);

    menu.className = 'dropdown-menu has-custom-scrollbar';
    menu.setAttribute('aria-labelledby', 'colorDropdownWishlist');

    appendColorGroup(menu, '', attr.otherRange && attr.otherRange.categoryheader && attr.otherRange.categoryheader.range, attr.otherArray);
    appendColorGroup(menu, attr.classicRange && attr.classicRange.categoryheader ? attr.classicRange.categoryheader.category + ' COLOR' : '', attr.classicRange && attr.classicRange.categoryheader && attr.classicRange.categoryheader.range, attr.classicArray);
    appendColorGroup(menu, attr.limitedRange && attr.limitedRange.categoryheader ? attr.limitedRange.categoryheader.category + ' COLOR' : '', attr.limitedRange && attr.limitedRange.categoryheader && attr.limitedRange.categoryheader.range, attr.limitedArray);
    appendColorGroup(menu, attr.saleRange && attr.saleRange.categoryheader ? attr.saleRange.categoryheader.category + ' COLOR' : '', attr.saleRange && attr.saleRange.categoryheader && attr.saleRange.categoryheader.range, attr.saleArray);
    appendColorGroup(menu, attr.clearanceRange && attr.clearanceRange.categoryheader ? attr.clearanceRange.categoryheader.category + ' COLOR' : '', attr.clearanceRange && attr.clearanceRange.categoryheader && attr.clearanceRange.categoryheader.range, attr.clearanceArray);

    selectedValue = menu.querySelector('[data-attr-value="' + selectedColor + '"]') || menu.querySelector('.js-dropdown-color.selected');
    if (selectedValue) {
        selectedValue.classList.add('selected');
        toggle.classList.add('selected');
        toggle.innerHTML = selectedValue.innerHTML;
    }

    dropdown.appendChild(toggle);
    dropdown.appendChild(menu);
    wrapper.appendChild(label);
    wrapper.appendChild(dropdown);

    return wrapper;
}

/**
 * Returns the wishlist size attribute section
 * @returns {jQuery} size section wrapper
 */
function getWishlistSizeSection() {
    return $('#addToWishlistDrawer [data-attr="size"]').first();
}

/**
 * Clears wishlist size validation feedback
 */
function clearWishlistSizeValidationError() {
    var $sizeSection = getWishlistSizeSection();
    if (!$sizeSection.length) {
        return;
    }
    $sizeSection.find('#wishlistSizeValidationMessage').empty();
    $sizeSection.find('#wishlistSizeApplyStatus').empty();
    $sizeSection.find('.sizes-container').removeClass('is-invalid').removeAttr('aria-invalid');
    $sizeSection.find('button.size-attribute').removeAttr('aria-invalid');
    $sizeSection.find('.size-value.selectable').removeClass('unselected');
}

/**
 * Shows wishlist size validation feedback when size is not selected
 */
function showWishlistSizeValidationError() {
    var $sizeSection = getWishlistSizeSection();
    var $sizesContainer = $sizeSection.find('.sizes-container');
    var $validationMessage = $sizeSection.find('#wishlistSizeValidationMessage');
    var $applyStatus = $sizeSection.find('#wishlistSizeApplyStatus');
    var message = $sizesContainer.data('msg-size-required') || '';
    var secondaryMessage = $sizesContainer.data('msg-make-selection') || '';

    $sizesContainer.find('.size-value.selectable').addClass('unselected');

    var $firstSelectable = $sizeSection.find('button.size-attribute').filter(function () {
        return $(this).find('.size-value.selectable').length > 0;
    }).first();

    $sizesContainer.addClass('is-invalid').attr('aria-invalid', 'true');
    $validationMessage.text(secondaryMessage);
    $applyStatus.empty();
    window.setTimeout(function () {
        $applyStatus.text(message);
    }, 100);

    $sizeSection.find('button.size-attribute').removeAttr('aria-invalid');
    if ($firstSelectable.length) {
        $firstSelectable.attr('aria-invalid', 'true');
        $firstSelectable.focus();
    }
}

/**
 * Renders the wishlist size selector from product variation data.
 * @param {Object} attr - Size variation attribute
 * @param {string|null} selectedSize - Currently selected size
 * @returns {HTMLDivElement} Rendered size attribute wrapper
 */
function renderSizeAttribute(attr, selectedSize) {
    var wrapper = document.createElement('div');
    var label = document.createElement('label');
    var sizeContainer = document.createElement('div');
    var validationMessage = document.createElement('div');
    var applyStatus = document.createElement('div');
    var msgSource = document.querySelector('#productWishlistVariations .wishlist-size-messages');

    wrapper.className = 'attribute mb-3';
    wrapper.setAttribute('data-attr', 'size');

    label.id = 'wishlistSizeLabel';
    label.className = attr.id + ' label';
    label.textContent = 'Size:';

    sizeContainer.className = 'wishlist-sizes-container selected-price-container sizes-container d-flex flex-wrap';
    sizeContainer.setAttribute('role', 'group');
    sizeContainer.setAttribute('aria-labelledby', 'wishlistSizeLabel');
    if (msgSource) {
        sizeContainer.setAttribute('data-msg-size-required', msgSource.getAttribute('data-msg-size-required') || '');
        sizeContainer.setAttribute('data-msg-make-selection', msgSource.getAttribute('data-msg-make-selection') || '');
    }

    (attr.values || []).forEach(function (attrValue) {
        var button = document.createElement('button');
        var span = document.createElement('span');

        button.className = 'btn btn-link size-attribute';
        button.setAttribute('aria-label', attr.displayName + ' ' + attrValue.displayValue);
        button.setAttribute('aria-describedby', 'wishlistSizeApplyStatus');
        button.setAttribute('data-url', attrValue.url || '');
        button.setAttribute('data-attr-value-id', attrValue.id || '');

        span.setAttribute('data-attr-value', attrValue.value);
        span.className = 'size-value'
            + ((selectedSize && attrValue.value === selectedSize) || attrValue.selected ? ' selected' : '')
            + (attrValue.selectable ? ' selectable' : ' unselectable');
        span.setAttribute('data-toggle', 'tooltip');
        span.setAttribute('data-placement', 'top');
        span.setAttribute('data-title', attrValue.selectable ? (attrValue.stockMessage || '') : (attrValue.noStockMessage || ''));
        span.setAttribute('data-selectable-title', attrValue.stockMessage || '');
        span.setAttribute('data-unselectable-title', attrValue.noStockMessage || '');
        span.textContent = attrValue.displayValue;

        button.appendChild(span);
        sizeContainer.appendChild(button);
    });

    validationMessage.id = 'wishlistSizeValidationMessage';
    validationMessage.className = 'invalid-feedback';

    applyStatus.id = 'wishlistSizeApplyStatus';
    applyStatus.className = 'sr-only';
    applyStatus.setAttribute('role', 'status');
    applyStatus.setAttribute('aria-live', 'polite');
    applyStatus.setAttribute('aria-atomic', 'true');

    wrapper.appendChild(label);
    wrapper.appendChild(sizeContainer);
    wrapper.appendChild(validationMessage);
    wrapper.appendChild(applyStatus);

    return wrapper;
}

/**
 * Renders the wishlist variation controls into the drawer.
 * @returns {boolean} True when variation markup was rendered
 */
function renderWishlistVariationContent() {
    var wishlistProductData = getCurrentWishlistProductData();
    var variationContainer = document.querySelector('.wishlist-variation-content');
    var selectedColorValue = getSelectedWishlistAttributeValue('color');
    var selectedSizeValue = getSelectedWishlistAttributeValue('size');
    var selectedColor = selectedColorValue ? selectedColorValue.value : null;
    var selectedSize = selectedSizeValue ? selectedSizeValue.value : null;

    if (!variationContainer || !wishlistProductData) {
        return false;
    }

    variationContainer.innerHTML = '';

    (wishlistProductData.variationAttributes || []).forEach(function (attr) {
        if (attr.id === 'color' && attr.swatchable) {
            variationContainer.appendChild(renderColorAttribute(attr, selectedColor));
        }

        if (attr.id === 'size') {
            variationContainer.appendChild(renderSizeAttribute(attr, selectedSize));
        }
    });

    return variationContainer.children.length > 0;
}

/**
 * Show added to wishlist popup
 * @param {string} data - data returned from the server's ajax call
 */
function displayMessage(data) {
    var $addToWishlistDrawer = $('#addToWishlistDrawer');
    var $productWishlistVariations = $('#productWishlistVariations');
    var $productAddedToWishlist = $('#productAddedToWishlist');

    if (data.success) {
        $productAddedToWishlist.removeClass('d-none');
        $productWishlistVariations.addClass('d-none');
    } else {
        var alertHtmlString = `
            <div class="alert alert-danger alert-dismissible fade show" role="alert">
            ` + data.msg + `
            <button type="button" class="close" data-dismiss="alert" aria-label="Close">
                <span aria-hidden="true">&times;</span>
            </button>
            </div>
        `;

        $addToWishlistDrawer.find('.modal-body').prepend(alertHtmlString);

        setTimeout(function () {
            $addToWishlistDrawer.find('.alert').remove();
        }, 10000);
    }
}

/**
 * Validate size is selected
 *
 * @return {boolean} - If size is selected or not
 */
function validateSizeSelected() {
    var $wishlistSizeSelect = $('.wishlist-sizes-container');

    if ($wishlistSizeSelect.length === 0) {
        return true;
    }

    if ($wishlistSizeSelect.find('.size-value.selected').length === 0) {
        showWishlistSizeValidationError();
        return false;
    }

    clearWishlistSizeValidationError();
    return true;
}

/**
 * Get product id
 *
 * @return {string} - the complete product id
 */
function getProductId() {
    var wishlistProductData = getCurrentWishlistProductData();
    var productId = $('#productId').val();

    if (!productId) {
        return null;
    }

    if (wishlistProductData && wishlistProductData.id) {
        return wishlistProductData.id;
    }

    return productId;
}

/**
 * Add product to Wishlist
 *
 */
function addToWishlist() {
    if (!validateSizeSelected()) {
        return;
    }

    var url = $('.add-to-wish-list').data('href');
    var pid = getProductId();
    var optionId = $('.add-to-wish-list').closest('.product-detail').find('.product-option').attr('data-option-id');
    var optionVal = $('.add-to-wish-list').closest('.product-detail').find('.options-select option:selected').attr('data-value-id');
    optionId = optionId || null;
    optionVal = optionVal || null;
    if (!url || !pid) {
        return;
    }

    $.spinner().start();

    $.ajax({
        url: url,
        type: 'post',
        dataType: 'json',
        data: {
            pid: pid,
            optionId: optionId,
            optionVal: optionVal
        },
        success: function (data) {
            displayMessage(data);
            $.spinner().stop();
        },
        error: function (err) {
            displayMessage(err);
            $.spinner().stop();
        }
    });
}

module.exports = {
    init: function () {
        pdpProductData = (window.product_data && typeof window.product_data === 'object' && !Array.isArray(window.product_data))
            ? window.product_data
            : null;

        $('body').on('product:afterAttributeSelect', function (e, response) {
            if (response && response.data && response.data.product && $('#quickViewModal.show').length === 0) {
                pdpProductData = response.data.product;
                if ($('#addToWishlistDrawer').hasClass('show')) {
                    currentWishlistProductData = pdpProductData;
                    renderWishlistVariationContent();
                }
            }
        });

        $('#addToWishlistDrawer').on('show.bs.modal', function () {
            currentWishlistProductData = getWishlistProductData();
            var hasRenderedAttributes = renderWishlistVariationContent();
            var $specialSelect = $('.wishlist-special-select');
            var $sizeSelect = $('.wishlist-sizes-container');

            // Add item to wishlist if it doesn't have variations
            if (!hasRenderedAttributes && $specialSelect.length === 0 && $sizeSelect.length === 0) {
                addToWishlist();
                return;
            }

            ensureWishlistDefaultSelection(function () {
                renderWishlistVariationContent();
            });
        });

        $('#addToWishlistDrawer').on('hidden.bs.modal', function () {
            currentWishlistProductData = null;
            $('#addToWishlistDrawer').find('.alert').remove();
            if ($('#productWishlistVariations').hasClass('d-none')) {
                $('#productWishlistVariations, #productAddedToWishlist').toggleClass('d-none');
            }

            // Clear size and color selection
            var $specialSelect = $('.wishlist-special-select');
            if ($specialSelect.length !== 0 && $specialSelect.find('.js-dropdown-color.selected').length !== 0) {
                $specialSelect.next('.make-select').empty();
                $specialSelect.find('.js-dropdown-color').each(function () {
                    $(this).removeClass('selected');
                    return;
                });
            }

            $('.wishlist-variation-content').empty();
        });

        $('#addToWishlistDrawer').on('click', function () {
            $('#addToWishlistDrawer').find('.alert').remove();
        });
    },

    selectColorWishlist: function () {
        $(document).on('click', '[data-attr="wishlist-color"] .wishlist-special-select button.js-dropdown-color', function (e) {
            e.preventDefault();
            var $this = $(this);

            if ($this.hasClass('disabled') || !$this.data('url')) {
                return;
            }

            $.spinner().start();
            updateWishlistProductDataFromUrl($this.data('url'), function (success) {
                $.spinner().stop();
                if (success) {
                    renderWishlistVariationContent();
                }
            });
        });
    },

    selectSizeWishlist: function () {
        $(document).on('click', '.wishlist-sizes-container button.size-attribute', function (e) {
            e.preventDefault();
            var $this = $(this);

            if ($this.find('.size-value').hasClass('unselectable') || !$this.data('url')) {
                return;
            }

            clearWishlistSizeValidationError();
            $.spinner().start();
            updateWishlistProductDataFromUrl($this.data('url'), function (success) {
                $.spinner().stop();
                if (success) {
                    renderWishlistVariationContent();
                }
            });
        });
    },

    addToWishlist: function () {
        $(document).on('click', '.add-to-wish-list', function (e) {
            e.preventDefault();
            addToWishlist();
        });
    }
};
